<template>
  <div
    data-testid="hero-report-card"
    class="flex h-full max-h-[312px] w-full flex-shrink-0 flex-col rounded-lg"
  >
    <div
      class="relative flex flex-grow flex-col justify-between rounded-lg bg-s-100 p-6"
    >
      <CardLabel
        v-if="fileType"
        class="absolute left-3 top-3 z-10"
        :type="fileType"
        :show-label="report?.show_label"
        :label="cardLabel"
      />

      <div class="absolute left-0 top-0 h-full w-full">
        <img
          data-testid="hero-report-card-image"
          class="h-full w-full rounded-lg object-cover object-center"
          :src="$cloudinary.resize(reportImage, 'hero-video-card')"
          alt=""
        />
        <div
          class="absolute left-0 top-0 h-full w-full rounded-lg bg-black/60"
        />
      </div>
      <div class="z-10 mt-12 text-white">
        <div
          data-testid="hero-report-title"
          class="line-clamp-3 text-xl font-bold"
        >
          {{ reportTitle }}
        </div>
      </div>
      <ReportCardFooter
        :report-name="reportName"
        :report-file="reportFile"
        :report-has-access="reportHasAccess"
        :initial-fields="initialFields"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import CardLabel from '~/components/portals/CardLabel.vue'
import ReportCardFooter from '~/components/portals/ReportCardFooter.vue'
import {
  FORECASTS,
  ESG,
  PRICE_ASSESSMENTS,
} from '~/constants/services/constants'
import { reformat } from '~/utils/date'
import { useNuxtApp } from '#app'
import type { TReportProp } from '~/src/types/reports'

const props = withDefaults(
  defineProps<{
    report?: TReportProp
    category?: string | null
  }>(),
  {
    report: null,
    category: null,
  },
)

const { $cloudinary } = useNuxtApp()
const cardLabel = computed(() => {
  if (!props.report) return

  return props.report.market_type === 'prices'
    ? reformat(props.report.date, 'dd MMM yyyy')
    : null
})

const reportTitle = computed(() => props.report?.title)
const reportName = computed(() => props.report?.file_name)
const reportImage = computed(() => props.report?.images?.desktop)

const fileType = computed(() => {
  const extension = props.report?.file_extension

  if (extension === 'pdf') {
    return 'pdf'
  }

  if (['xls', 'xlsm', 'xlsx', 'csv'].includes(extension)) {
    return 'xlsx'
  }

  if (extension === 'pptx' || extension === 'ppt') {
    return 'ppt'
  }

  return ''
})

const reportFile = computed(() => {
  return { ...props.report, category: props.category }
})

const reportHasAccess = computed(() => props.report?.has_access)

const initialFields = computed(() => {
  const initialFields = [props.category]
  const marketType = props.report?.market_type
  if (marketType === 'forecast') {
    initialFields.push(FORECASTS.checkbox)
  } else if (marketType === 'esg') {
    initialFields.push(ESG.checkbox)
  } else {
    initialFields.push(PRICE_ASSESSMENTS.checkbox)
  }
  return initialFields
})
</script>
